import { Component, OnInit } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';

import {
  ShipmentGeofenceViewModel,
  ShipmentLocationListTableComponent,
  ShipmentLocationViewModel,
} from './shipment-location-list-table/shipment-location-list-table.component';

import { ShipmentLocationService } from '@services/api/shipment-location.service';
import { ShipmentLocationMapComponent } from './shipment-location-map/shipment-location-map.component';
import {
  GeofenceType,
  ShipmentLocationEditDialogComponent,
  ShipmentLocationEditViewModel,
  UnitType,
} from './shipment-location-edit-dialog/shipment-location-edit-dialog.component';
import { firstValueFrom } from 'rxjs';
import {
  DEFAULT_RADIUS_METERS,
  ImportShipmentLocationAddRequest,
  ShipmentGeofenceDto,
  ShipmentLocationAddRequest,
  ShipmentLocationBulkDeleteResponse,
  ShipmentLocationBulkImportResponse,
  ShipmentLocationImportErrorResponse,
  ShipmentLocationUpdateRequest,
} from '@entities/shipment-location';
import { ExportService } from '@services/export.service';
import { format } from 'date-fns';

import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import {
  ShipmentLocationBulkAddDialogComponent,
  ShipmentLocationImportViewModel,
} from './shipment-location-bulk-add-dialog/shipment-location-bulk-add-dialog.component';
import { DeviceInfoService } from '@services/api/device-info.service';
import {
  ShipmentLocationImportErrorDialogComponent,
  ShipmentLocationImportErrorDialogOption,
  ShipmentLocationImportFailureViewModel,
} from './shipment-location-import-error-dialog/shipment-location-import-error-dialog.component';
import { ShipmentLocationDeviceViewModel } from './shipment-location-map/shipment-location-map.component';

@Component({
  selector: 'app-shipment-location-list',
  standalone: true,
  imports: [
    MatCardModule,
    ShipmentLocationListTableComponent,
    ShipmentLocationMapComponent,
  ],
  templateUrl: './shipment-location-list.component.html',
  styleUrl: './shipment-location-list.component.scss',
})
export class ShipmentLocationListComponent implements OnInit {
  /** 位置情報一覧 */
  public locationList: ShipmentLocationViewModel[] = [];

  /** CSVのヘッダーを定義するためのマップ */
  private readonly csvColumnMap = [
    { key: 'locationName', label: 'Location Name' },
    { key: 'address', label: 'Address' },
    { key: 'postalCode', label: 'Postal Code' },
    { key: 'latitude', label: 'Latitude' },
    { key: 'longitude', label: 'Longitude' },
  ];

  /** 選択された位置情報 */
  public selectedLocation: ShipmentLocationViewModel | undefined;

  constructor(
    private shipmentLocationService: ShipmentLocationService,
    private deviceInfoService: DeviceInfoService,
    private exportService: ExportService,
    private dialog: MatDialog,
  ) {}

  public ngOnInit(): void {
    // 位置情報一覧を初期化する
    this.initLocationList();
  }

  /**
   * 位置情報追加/編集ダイアログを表示する
   * @param location 位置情報
   */
  public async openShipmentLocationEditDialog(
    location?: ShipmentLocationViewModel,
  ) {
    const deviceLocationList = await this.loadDeviceLocationList();

    const options: ShipmentLocationEditViewModel = {
      updating: false,
      address: '',
      locationName: '',
      geofenceType: 'circle',
      circleRadius: DEFAULT_RADIUS_METERS,
      circleRadiusUnit: 'm',
      circleRadiusInMeters: DEFAULT_RADIUS_METERS,
      polygonList: [],
      numberOfVertices: 3,
      latitude: 0,
      longitude: 0,
      postalCode: '',
      isUse: location?.isUse ?? false,
      isShipping: location?.isShipping ?? false,
      registeredLocationList: this.locationList,
      deviceLocationList: deviceLocationList,
    };

    if (location) {
      const geofence = location.geofence;

      // geofenceが存在する場合にgeofenceTypeを取得
      const geofenceType: GeofenceType = geofence?.geofenceType ?? 'circle';
      options.geofenceType = geofenceType;

      // geofenceTypeに応じてcircleRadiusまたはpolygonListを取得
      if (geofence?.geofenceType === 'circle') {
        options.circleRadius = geofence.circleRadius;
        options.circleRadiusUnit = geofence.circleRadiusUnit as UnitType;
        options.circleRadiusInMeters = geofence.circleRadiusInMeters;
      } else if (geofence?.geofenceType === 'polygon') {
        // polygonListの最初と最後は同じ頂点なので、頂点数としては1つ減らす
        options.numberOfVertices = geofence.polygonList.length - 1;
        options.polygonList = geofence.polygonList;
      }

      options.updating = true;
      options.address = location.address ?? '';
      options.locationName = location.locationName;
      options.latitude = location.latitude;
      options.longitude = location.longitude;
      options.postalCode = location.postalCode || '';
      options.registeredLocationList = this.locationList.filter(
        (loc) => loc.locationId !== location.locationId,
      );
    }

    const editedLocation: ShipmentLocationEditViewModel = await firstValueFrom(
      this.dialog
        .open(ShipmentLocationEditDialogComponent, {
          width: '80%',
          height: '90%',
          minWidth: '500px',
          data: options,
        })
        .afterClosed(),
    );

    // 更新/追加をキャンセルするときは更新/追加処理をしない
    if (!editedLocation) {
      return;
    }

    // 更新/追加するジオメトリー
    const editedGeometry: ShipmentGeofenceDto = {
      geofenceType: editedLocation.geofenceType,
    };
    if (editedGeometry.geofenceType === 'circle') {
      editedGeometry.circleRadius = editedLocation.circleRadius;
      editedGeometry.circleRadiusUnit = editedLocation.circleRadiusUnit;
      editedGeometry.circleRadiusInMeters = editedLocation.circleRadiusInMeters;
    } else {
      editedGeometry.polygonList = editedLocation.polygonList;
    }

    if (options.updating && location) {
      // 更新処理
      const updatedLocationRequest: ShipmentLocationUpdateRequest = {
        locationId: location.locationId,
        locationName: editedLocation.locationName,
        latitude: editedLocation.latitude,
        longitude: editedLocation.longitude,
        address: editedLocation.address,
        geometry: editedGeometry,
        postalCode: editedLocation.postalCode,
      };
      this.shipmentLocationService
        .update(updatedLocationRequest.locationId, updatedLocationRequest)
        .subscribe({
          next: () => {
            // ロケーション一覧を再取得する
            this.initLocationList();
          },
          error: (error) => {
            let message = '';
            if (error.response.status === 400) {
              message = `位置情報名もしくは住所が重複しているため、\n別の位置情報名もしくは住所でもう一度お試しください。`;
            } else {
              message = `位置情報の変更に失敗しました。\nしばらくしてからもう一度お試しください。`;
            }

            const alertDialogOptions: AlertDialogOption = {
              type: 'error',
              message: message,
            };
            this.dialog.open(AlertDialogComponent, {
              width: '600px',
              data: alertDialogOptions,
            });
          },
        });
    } else {
      // ロケーション追加処理
      const addedLocationRequest: ShipmentLocationAddRequest = {
        locationName: editedLocation.locationName,
        latitude: editedLocation.latitude,
        longitude: editedLocation.longitude,
        address: editedLocation.address,
        geometry: editedGeometry,
        postalCode: editedLocation.postalCode,
      };
      this.shipmentLocationService.add(addedLocationRequest).subscribe({
        next: () => {
          // ロケーション一覧を再取得する
          this.initLocationList();
        },
        error: (error) => {
          let message = '';
          if (error.response.status === 400) {
            message = `位置情報名もしくは住所が重複しているため、\n別の位置情報名もしくは住所でもう一度お試しください。`;
          } else {
            message = `位置情報追加に失敗しました。\nしばらくしてからもう一度お試しください。`;
          }

          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    }
  }

  /**
   * 位置情報が選択された時
   * @param location 位置情報
   */
  public onSelectedLocation(location: ShipmentLocationViewModel | undefined) {
    this.selectedLocation = location;
  }

  /**
   * 位置情報インポートダイアログを表示する
   * CSVから読み込んだ位置情報を一括登録する
   */
  public async openShipmentLocationImportDialog(): Promise<void> {
    // CSVインポートダイアログを表示し、読み込み結果を受け取る
    const bulkImportLocations: ShipmentLocationImportViewModel[] | null =
      await firstValueFrom(
        this.dialog
          .open(ShipmentLocationBulkAddDialogComponent, {
            width: '600px',
          })
          .afterClosed(),
      );

    if (!bulkImportLocations) {
      return;
    }

    // API仕様に合わせ、空文字をnullへ変換してリクエストを構築する
    const requestList: ImportShipmentLocationAddRequest[] =
      bulkImportLocations.map((location) => ({
        locationName: this.normalizeShipmentLocationImportText(
          location.locationName,
        ),
        address: this.normalizeShipmentLocationImportText(location.address),
        longitude: this.parseShipmentLocationImportNumber(location.longitude),
        latitude: this.parseShipmentLocationImportNumber(location.latitude),
      }));

    this.shipmentLocationService.bulkImport(requestList).subscribe({
      next: (response) => {
        // APIのカテゴリ別エラーを画面表示用に統合する
        const allErrors = this.buildShipmentLocationImportErrors(response);

        // 位置情報設定一覧を再取得する
        this.initLocationList();

        if (allErrors.length > 0) {
          const errorDialogOptions: ShipmentLocationImportErrorDialogOption = {
            type: 'warn',
            message:
              '以下の位置情報の登録をスキップしました。\n原因を修正のうえ、再度登録を行ってください。',
            title: 'Warning',
            data: allErrors,
          };
          this.dialog.open(ShipmentLocationImportErrorDialogComponent, {
            width: '1000px',
            data: errorDialogOptions,
          });
        }
      },
      error: () => {
        // 通信エラーや想定外エラー時は共通エラーダイアログを表示する
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message:
            '位置情報の一括登録に失敗しました。\nしばらくしてからもう一度お試しください。',
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  /**
   * CSVエクスポートを行う
   * @param locationList 画面上にある位置情報一覧
   */
  public exportToCsv() {
    try {
      const csvData = this.locationList.map((location) => ({
        locationName: location.locationName,
        address: location.address ?? '',
        postalCode: location.postalCode ?? '',
        latitude: location.latitude,
        longitude: location.longitude,
      }));

      const formattedDate = format(new Date(), 'yyyyMMddHHmmss');
      const fileName = `locations_${formattedDate}.csv`;

      this.exportService.exportToCsv(
        this.csvColumnMap,
        csvData,
        fileName,
        true,
        "yyyy-MM-dd'T'HH:mm:ssXXX",
        '',
        'Asia/Tokyo',
        ['postalCode'],
      );
    } catch (error) {
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: `CSVのエクスポートに失敗しました。\nしばらくしてからもう一度お試しください。`,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
    }
  }

  /**
   * 位置情報を削除する
   * @param location 位置情報Id
   */
  public async deleteLocation(location: ShipmentLocationViewModel) {
    // 位置情報が使用されている配送・ルートがある場合は削除できない
    if (location.isUse) {
      const message =
        '選択した位置情報は既にルートや配送に使用されています。\n先に使用しているルートや配送を削除してください。';
      const alertDialogOptions: AlertDialogOption = {
        type: 'warning',
        message: message,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '650px',
        data: alertDialogOptions,
      });
      return;
    }

    const locationId = location.locationId;
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `選択した位置情報設定を削除してよろしいですか？`,
      operation: '削除',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    this.shipmentLocationService.delete(locationId).subscribe({
      next: () => {
        // 位置情報設定一覧を再取得する
        this.initLocationList();
      },
      error: () => {
        const message = `位置情報設定削除に失敗しました。\nしばらくしてからもう一度お試しください。`;

        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: message,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  /**
   * 配送位置情報を一括削除する
   * @param selectedLocationList 選択された配送位置情報
   */
  public async deleteLocations(selectedLocationList: ShipmentLocationViewModel[]) {
    if (!selectedLocationList || selectedLocationList.length === 0) {
      return;
    }

    if (selectedLocationList.length === 1) {
      await this.deleteLocation(selectedLocationList[0]);
      return;
    }

    const deletableLocationList = selectedLocationList.filter(
      (location) => !location.isUse && !location.isShipping,
    );
    const undeletableCount =
      selectedLocationList.length - deletableLocationList.length;

    if (deletableLocationList.length === 0) {
      const alertDialogOptions: AlertDialogOption = {
        type: 'warning',
        message:
          '選択した位置情報設定はルートまたは配送中の配送設定で使用されているため削除できません。',
      };
      this.dialog.open(AlertDialogComponent, {
        width: '650px',
        data: alertDialogOptions,
      });
      return;
    }

    let confirmMessage =
      `選択した位置情報設定 ${selectedLocationList.length} 件のうち、` +
      `${deletableLocationList.length} 件を削除します。よろしいですか？`;
    if (undeletableCount > 0) {
      confirmMessage += `\n${undeletableCount} 件は使用中のため削除対象から除外されます。`;
    }

    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: confirmMessage,
      operation: '削除',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    this.shipmentLocationService
      .deleteBulk({
        locationId: deletableLocationList.map((location) => location.locationId),
      })
      .subscribe({
        next: (response: ShipmentLocationBulkDeleteResponse) => {
          this.initLocationList();

          const successCount = response.successLocationIdList.length;
          const failedCountByApi = response.failedLocationIdList.length;
          const failedTotalCount = failedCountByApi + undeletableCount;

          let message = '';
          let type: 'info' | 'warning' = 'info';
          if (failedTotalCount === 0) {
            message = `位置情報設定 ${successCount} 件を削除しました。`;
          } else if (successCount === 0) {
            type = 'warning';
            message =
              `位置情報設定の削除に失敗しました。\n` +
              `選択 ${selectedLocationList.length} 件中 ${failedTotalCount} 件が削除できませんでした。`;
          } else {
            type = 'warning';
            message =
              `位置情報設定の一括削除が完了しました。\n` +
              `${selectedLocationList.length} 件中 ${successCount} 件を削除、` +
              `${failedTotalCount} 件は削除できませんでした。`;
          }

          const alertDialogOptions: AlertDialogOption = {
            type,
            message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '650px',
            data: alertDialogOptions,
          });
        },
        error: () => {
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message:
              '位置情報設定の一括削除に失敗しました。\nしばらくしてからもう一度お試しください。',
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
  }

  /**
   * インポート文字列を正規化する
   * @param value CSVのセル値
   * @returns 空文字はnull、それ以外はtrim済み文字列
   */
  private normalizeShipmentLocationImportText(
    value: string | null | undefined,
  ): string | null {
    const trimmedValue = (value ?? '').trim();
    return trimmedValue === '' ? null : trimmedValue;
  }

  /**
   * インポート数値を正規化する
   * @param value CSVのセル値
   * @returns 数値に変換できる場合はnumber、できない/空の場合はnull
   */
  private parseShipmentLocationImportNumber(
    value: string | number | null | undefined,
  ): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const trimmedValue = (value ?? '').toString().trim();
    if (trimmedValue === '') {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  /**
   * APIレスポンスのエラー一覧を画面表示用に変換する
   * @param response 一括インポートAPIレスポンス
   * @returns 行番号でソート済みの失敗一覧
   */
  private buildShipmentLocationImportErrors(
    response: ShipmentLocationBulkImportResponse,
  ): ShipmentLocationImportFailureViewModel[] {
    const mapErrors = (
      errorMap: { [key: string]: ShipmentLocationImportErrorResponse },
      getMessage: (location: ShipmentLocationImportErrorResponse) => string,
    ): ShipmentLocationImportFailureViewModel[] =>
      Object.entries(errorMap).map(([csvNo, location]) => ({
        csvNo,
        locationName: location.locationName ?? '',
        message: getMessage(location),
      }));

    // 必須不足などの入力不備
    const invalidErrors = mapErrors(
      response.errorInvalidLocations,
      (location) =>
        location.errorReason === 'required_fields_missing'
          ? '必須項目が不足しています。'
          : 'データ形式が不正です。',
    );
    // DB既存データ、または同一リクエスト内での重複
    const duplicateErrors = mapErrors(
      response.errorDuplicateLocations,
      () => '位置情報が重複しています。',
    );
    // 緯度経度から住所を補完できなかったデータ
    const reverseGeocodeErrors = mapErrors(
      response.errorReverseGeocodeLocations,
      () => '緯度・経度から住所を特定できませんでした。',
    );
    // ジオフェンスを登録できなかったデータ
    const failRegistrationErrors = mapErrors(
      response.errorGeofenceRegistrationLocations,
      () => 'ジオフェンスの登録に失敗しました。',
    );

    const allErrors = [
      ...invalidErrors,
      ...duplicateErrors,
      ...reverseGeocodeErrors,
      ...failRegistrationErrors,
    ];

    // 行番号昇順にしてダイアログで見やすくする
    allErrors.sort((a, b) => {
      const aCsvNo = parseInt(a.csvNo, 10);
      const bCsvNo = parseInt(b.csvNo, 10);

      if (Number.isNaN(aCsvNo) || Number.isNaN(bCsvNo)) {
        return a.csvNo.localeCompare(b.csvNo);
      }

      return aCsvNo - bCsvNo;
    });

    return allErrors;
  }

  private initLocationList() {
    this.shipmentLocationService.findList().subscribe({
      next: (locationList) => {
        this.locationList = locationList.map((location) => {
          let geofence: ShipmentGeofenceViewModel | undefined;
          if (location.geometry?.geofenceType === 'circle') {
            geofence = {
              geofenceType: 'circle',
              circleRadius: location.geometry?.circleRadius || 0,
              circleRadiusUnit: location.geometry?.circleRadiusUnit || 'm',
              circleRadiusInMeters:
                location.geometry?.circleRadiusInMeters || 0,
            };
          } else if (location.geometry?.geofenceType === 'polygon') {
            geofence = {
              geofenceType: 'polygon',
              polygonList: location.geometry?.polygonList || [],
            };
          }

          return { ...location, geofence: geofence };
        });
      },
    });
  }

  private async loadDeviceLocationList(): Promise<
    ShipmentLocationDeviceViewModel[]
  > {
    try {
      const deviceList = await firstValueFrom(
        this.deviceInfoService.findDeviceList(),
      );
      console.log(
        '[ShipmentLocationList] findDeviceList response',
        deviceList,
      );

      const deviceLocationList = deviceList
        .filter(
          (device) =>
            device.location?.longitude !== undefined &&
            device.location?.latitude !== undefined,
        )
        .map((device) => ({
          deviceName: device.deviceName,
          serialNo: device.deviceId,
          longitude: device.location!.longitude!,
          latitude: device.location!.latitude!,
        }));
      console.log(
        '[ShipmentLocationList] mapped deviceLocationList',
        deviceLocationList,
      );

      return deviceLocationList;
    } catch {
      console.error(
        '[ShipmentLocationList] findDeviceList failed. deviceLocationList is empty.',
      );
      return [];
    }
  }
}
